import { ApolloError } from "@apollo/client";
import { useGetOnpremiseConfigForCareseeker } from "apollo/hooks/queries";
import type { OnpremiseConfig, QueryProgress } from "core/types";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useLoggedCareseeker } from "reduxentities/hooks";

type OnPremiseSetupConfigContextValue = {
  error: ApolloError | undefined;
  onpremiseConfig: OnpremiseConfig | undefined;
  queryProgress: QueryProgress;
  refetch: () => Promise<unknown>;
};

const OnPremiseSetupConfigContext =
  createContext<OnPremiseSetupConfigContextValue | null>(null);

export function OnPremiseSetupConfigProvider({
  children,
}: {
  children: ReactNode;
}) {
  const loggedCareseeker = useLoggedCareseeker();
  const [queryProgress, onpremiseConfig, { error, refetch }] =
    useGetOnpremiseConfigForCareseeker({
      careseekerId: loggedCareseeker?.id,
    });

  const value = useMemo(
    () => ({
      error,
      onpremiseConfig,
      queryProgress,
      refetch,
    }),
    [error, onpremiseConfig, queryProgress, refetch],
  );

  return (
    <OnPremiseSetupConfigContext.Provider value={value}>
      {children}
    </OnPremiseSetupConfigContext.Provider>
  );
}

export function useOnPremiseSetupConfig() {
  const context = useContext(OnPremiseSetupConfigContext);
  if (!context) {
    throw new Error(
      "useOnPremiseSetupConfig must be used within OnPremiseSetupConfigProvider",
    );
  }
  return context;
}
